import { useState } from 'react';
import { Hash, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { api } from '../../api';
import { Button } from '../ui/button';
import { Sheet } from '../ui/side-sheet';
import type { MenuItem } from '../ui/menu';
import slackLogo from '../../assets/logos/slack.svg';
import { IntegrationCard, Logo, type CardMessage, type Fact, BlockedAction } from './IntegrationCard';
import { slackHealth, type SlackState } from './model';

type Channel = { id: string; name: string; private: boolean; member: boolean };
const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';
const ids = (value: string) => value.split(',').map(s => s.trim()).filter(Boolean);

/** Install Alder in Slack, choose the private AR channel (in a sheet), and send a test message. */
export function SlackCard({ state, onChanged, sendMode }: { state: SlackState; onChanged: () => Promise<void>;
  sendMode?: 'approve' | 'autonomous' | 'mixed' }) {
  const installation = state.installation;
  const bot = state.botName || 'Alder';
  const [open, setOpen] = useState(false); const [channels, setChannels] = useState<Channel[] | null>(null);
  const [channel, setChannel] = useState(''); const [allow, setAllow] = useState(''); const [dm, setDM] = useState('');
  const [busy, setBusy] = useState(false); const [formError, setFormError] = useState('');
  const [result, setResult] = useState<CardMessage>(null);
  const eligible = (channels ?? []).filter(c => c.private && c.member);

  async function loadChannels() { setChannels(await api<Channel[]>('/app-api/slack/channels')); }
  async function run(work: () => Promise<void>, onError: (text: string) => void) {
    setBusy(true);
    try { await work(); } catch (e) { onError(message(e)); } finally { setBusy(false); }
  }
  function openSheet() {
    setChannel(installation?.channel_id ?? ''); setAllow(installation?.approver_allowlist.join(', ') ?? '');
    setDM(installation?.dm_allowlist.join(', ') ?? ''); setFormError(''); setChannels(null); setOpen(true);
    void run(loadChannels, setFormError);
  }
  const connect = () => void run(async () => {
    const response = await api<{ url: string }>('/app-api/slack/connect', {}); window.location.assign(response.url);
  }, text => setResult({ kind: 'error', text }));
  const save = () => void run(async () => {
    await api('/app-api/slack/channel', { channelId: channel, approverAllowlist: ids(allow), dmAllowlist: ids(dm) });
    setOpen(false); await onChanged();
    setResult({ kind: 'success', text: 'AR channel saved. Send a test message to check it.' });
  }, setFormError);
  const test = () => void run(async () => {
    await api('/app-api/slack/test', { requestId: crypto.randomUUID() });
    setResult({ kind: 'success', text: 'Test message sent. It is in your AR channel now.' });
  }, text => setResult({ kind: 'error', text }));

  const keyFacts: Fact[] = installation ? [
    { label: 'Workspace', value: <><strong>{installation.team_name}</strong>
      {installation.mode === 'mock' && <span className="env-badge env-sandbox">Demo</span>}</> },
    { label: 'AR channel', value: installation.channel_name ? `#${installation.channel_name}` : 'Not chosen yet' },
  ] : [];
  const facts: Fact[] = installation ? [
    { label: 'Approvers', value: installation.approver_allowlist.length
      ? `${installation.approver_allowlist.length} named people` : 'Every channel member' },
    { label: 'Direct messages', value: installation.dm_allowlist.length
      ? `${installation.dm_allowlist.length} named people` : 'Nobody' },
  ] : [];
  const summary = !installation
    ? state.configured ? 'Answer questions and approvals where your team already works. About 2 minutes.' : undefined
    : sendMode === 'autonomous' ? 'Alder posts exceptions and questions in your AR channel; routine work goes out on its own.'
      : 'Alder posts questions and emails to approve in your AR channel.';
  const primary = !installation
    ? !state.configured ? <BlockedAction/> : <Button size="sm" disabled={busy} onClick={connect}>Add Alder to Slack</Button>
    : !installation.channel_id ? <Button size="sm" onClick={openSheet}><Hash aria-hidden/>Choose AR channel</Button>
      : <Button variant="outline" size="sm" disabled={busy} onClick={test}><Send aria-hidden/>Send test message</Button>;
  const menu: MenuItem[] = installation ? [
    ...(installation.channel_id ? [{ label: 'Channel and approvers', icon: <Hash size={15} aria-hidden/>, onSelect: openSheet }] : []),
    { label: 'Reconnect Slack', icon: <RotateCcw size={15} aria-hidden/>, disabled: busy || !state.configured, onSelect: connect },
  ] : [];

  return <>
    <IntegrationCard id="slack" logo={slackLogo} name="Slack" role="Approvals and questions"
      health={slackHealth(state)} summary={summary} keyFacts={keyFacts} facts={facts} message={result} primary={primary} menu={menu}
      reassurance="Membership of the AR channel is checked for every decision."/>
    <Sheet open={open} onClose={() => setOpen(false)} title="AR channel and approvers" icon={<Logo src={slackLogo}/>}
      description={`Alder posts questions and approvals in one private channel. Only channels ${bot} belongs to appear here.`}
      footer={<>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={busy || !channel} onClick={save}>{busy ? 'Saving…' : 'Save channel'}</Button>
      </>}>
      <form className="sheet-form" onSubmit={event => { event.preventDefault(); save(); }}>
        <label><span>Private channel</span>
          <select value={channel} onChange={event => setChannel(event.target.value)} disabled={channels === null}>
            <option value="">{channels === null ? 'Loading channels…' : 'Choose a channel'}</option>
            {eligible.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          {channels !== null && !eligible.length && <small role="status">
            No private channel has {bot} yet. Create a private channel in Slack and type /invite @{bot}, then refresh.</small>}
        </label>
        <Button type="button" variant="ghost" size="sm" className="self-start" disabled={busy}
          onClick={() => void run(loadChannels, setFormError)}><RefreshCw aria-hidden/>Refresh channels</Button>
        <label><span>Limit approvers to these Slack user IDs (optional)</span>
          <input value={allow} onChange={event => setAllow(event.target.value)} placeholder="U0123, U0456"/>
          <small>Leave empty to let every current channel member approve.</small></label>
        <label><span>Also allow these people to ask in DMs (optional)</span>
          <input value={dm} onChange={event => setDM(event.target.value)} placeholder="U0789"/></label>
        {formError && <p className="integration-error" role="alert">{formError}</p>}
        <button type="submit" hidden/>
      </form>
    </Sheet>
  </>;
}
